import classNames from 'classnames';
import Loader from 'components/Loader';
import Button from 'components/ui-kits/Button';
import { TrashBinOutline } from 'components/ui-kits/Icon';
import { SUPPORTED_FILE_EXTENSIONS } from 'const/Const';
import { MERCHANT_REGISTRATION_MESSAGE } from 'const/Message';
import { id } from 'date-fns/locale';
import React, { useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  uploadMerchantDocument,
  deleteMerchantDocument
} from 'services/documentService';
import { MessageErrorName } from 'styles/common';
import { DocumentId, DocumentUploadId, UploadedDocument } from 'types/Document';
import { handleError, handleSuccess } from 'utils';

declare global {
  interface Window {
    showOpenFilePicker: (options?: any) => Promise<any>;
  }
}

type UploadMerchantDocumentProps = {
  uploadedDocument: UploadedDocument | undefined;
  type: DocumentUploadId;
  screenName: DocumentId['screen'];
  isDisabled: boolean;
};

const UploadMerchantDocument = ({
  uploadedDocument,
  type,
  screenName,
  isDisabled
}: UploadMerchantDocumentProps) => {
  const inputRef = useRef<React.ElementRef<'input'>>(null);
  const queryClient = useQueryClient();
  const [selectedFileName, setSelectedFileName] = useState<string | undefined>(
    uploadedDocument?.originalName
  );
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  );

  const { isLoading, mutateAsync: uploadDocumentAsync } = useMutation(
    ['uploadDocument', id],
    async (file: File) => {
      const fd = new FormData();

      fd.append('file', file);

      const res = await uploadMerchantDocument(type, fd);

      return res;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['merchantDocuments', screenName]
        });
      }
    }
  );
  const {
    isLoading: isDeletingDocument,
    mutateAsync: deleteMerchantDocumentAsync
  } = useMutation(
    ['deleteMerchantDocument'],
    async (id: number) => deleteMerchantDocument(id),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: ['merchantDocuments', screenName]
        })
    }
  );

  const validateFile = (file: File | undefined) => {
    if (!file) {
      return 'This field is required';
    }

    if (file.size > 5_000_000) {
      return 'Fichier trop volumineux.';
    }

    const fileExtension = file.name.split('.').pop() ?? '';

    return SUPPORTED_FILE_EXTENSIONS.includes(fileExtension.toLowerCase())
      ? undefined
      : 'Format de fichier non supporté.';
  };

  const handleFileChanged = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isDisabled) {
      return;
    }

    try {
      const file = event.target.files?.[0];

      const errorMessage = validateFile(file);

      if (errorMessage) {
        // show to the user which file (if any)
        // is not meeting the criteria.
        setSelectedFileName(file?.name);
        setValidationError(errorMessage);

        return;
      }

      await uploadDocumentAsync(file!);
      handleSuccess(MERCHANT_REGISTRATION_MESSAGE.UPLOAD_DOCUMENT_SUCCESS);
    } catch (error) {
      if (uploadedDocument) {
        return;
      }

      setSelectedFileName('');
      handleError(undefined, MERCHANT_REGISTRATION_MESSAGE.GENERAL_ERROR);
    }
  };

  const handleDeleteFile = async () => {
    if (!uploadedDocument?.id || isDisabled) {
      return;
    }

    await deleteMerchantDocumentAsync(uploadedDocument.id);
    handleSuccess(MERCHANT_REGISTRATION_MESSAGE.DELETE_DOCUMENT_SUCCESS);
  };

  const shouldShowLoader = isDeletingDocument || isLoading;

  return (
    <>
      {shouldShowLoader && <Loader />}
      <Button
        isDisabled={isDisabled}
        variant="primary"
        onClick={() => inputRef.current?.click()}
        borderRadius="10rem"
        className={classNames('upload-document', { disabled: isDisabled })}>
        Télécharger
      </Button>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/*,.pdf,.doc,.docx"
        multiple={false}
        name={type}
        id={type}
        onChange={handleFileChanged}
      />
      <div>
        <a
          href={uploadedDocument?.documentPath}
          target="_blank"
          rel="noreferrer">
          {selectedFileName}
        </a>
        {!!validationError && (
          <MessageErrorName>{validationError}</MessageErrorName>
        )}
      </div>
      {!!uploadedDocument && (
        <button
          className={classNames('delete-document', { disabled: isDisabled })}
          type="button"
          onClick={handleDeleteFile}>
          <TrashBinOutline />
        </button>
      )}
    </>
  );
};

export default UploadMerchantDocument;
